import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Lightbulb, TrendingUp } from "lucide-react";
import { api } from "../../api";
import { MARKETING } from "../../constants/testIds";
import { GymLoader } from "../common/GymLoader";
import { ErrorState } from "../common/ErrorState";
import { StatusPill } from "../common/StatusPill";

const rupees = (paise) => (paise == null ? "—" : `₹${Math.round(paise / 100).toLocaleString("en-IN")}`);
const STATUS_PILL = { active: "active", paused: "expiring", draft: "expiring", completed: "active", archived: "overdue" };

// The number Meta and Google can't give you: what your ad spend actually returned in membership
// revenue, and whether those members stayed. Both sides come from records this system already
// holds — the append-only ledger and the membership history — so nothing here is estimated.
export function AttributionTab() {
  const q = useQuery({ queryKey: ["marketing-attribution"], queryFn: async () => (await api.get("/marketing/attribution")).data });

  if (q.isLoading) return <GymLoader label="Working out what your ads returned…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} title="Couldn't load attribution" />;

  const { totals, campaigns, insight, alerts } = q.data;
  const profitable = (totals.roi_multiple || 0) >= 1;
  const earning = totals.spend > 0;

  return (
    <div className="space-y-4">
      <div
        data-testid={MARKETING.attributionHeadline}
        className="rounded-[14px] p-5"
        style={{
          background: earning && profitable ? "rgba(5,150,105,0.08)" : "var(--scarlet-surface)",
          border: `1px solid ${earning && profitable ? "rgba(5,150,105,0.35)" : "var(--scarlet-hairline)"}`,
        }}
      >
        {earning ? (
          <>
            <p className="eyebrow flex items-center gap-1.5"><TrendingUp size={12} /> Return on ad spend</p>
            <p className="text-3xl font-bold mt-1.5 tabular-nums" style={{ color: profitable ? "var(--scarlet-pos)" : "var(--scarlet-text)" }}>
              {totals.roi_multiple != null ? `${totals.roi_multiple}×` : "—"}
            </p>
            <p className="text-sm mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
              {rupees(totals.spend)} spent → <strong style={{ color: "var(--scarlet-text)" }}>{rupees(totals.revenue)}</strong> in
              real membership revenue from {totals.converted_members} member{totals.converted_members === 1 ? "" : "s"}.
            </p>
            <p className="text-xs mt-1.5" style={{ color: "var(--scarlet-text-secondary)" }}>
              Net {rupees(totals.net)} · counted from payments actually received, less refunds.
            </p>
          </>
        ) : (
          <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
            Log some ad spend against a campaign and this will show exactly what it returned.
          </p>
        )}
      </div>

      {alerts?.map((a) => (
        <div key={a} data-testid={MARKETING.attributionAlert} className="rounded-[12px] p-3.5 flex gap-2.5"
          style={{ background: "rgba(180,35,24,0.07)", border: "1px solid rgba(180,35,24,0.28)" }}>
          <AlertTriangle size={16} className="shrink-0 mt-0.5" style={{ color: "var(--scarlet-neg)" }} />
          <p className="text-sm" style={{ color: "var(--scarlet-text)" }}>{a}</p>
        </div>
      ))}

      {insight && (
        <div data-testid={MARKETING.attributionInsight} className="rounded-[12px] p-3.5 flex gap-2.5"
          style={{ background: "var(--scarlet-accent-tint)", border: "1px solid var(--scarlet-hairline)" }}>
          <Lightbulb size={16} className="shrink-0 mt-0.5" style={{ color: "var(--scarlet-accent)" }} />
          <p className="text-sm" style={{ color: "var(--scarlet-text)" }}>{insight}</p>
        </div>
      )}

      <div className="space-y-3">
        {campaigns.filter((c) => c.attribution.spend > 0 || c.attribution.converted_members > 0).map((c) => {
          const a = c.attribution;
          const r = a.retention;
          return (
            <div key={c.id} data-testid={MARKETING.attributionRow(c.id)} className="solid-surface rounded-[14px] p-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-semibold text-sm" style={{ color: "var(--scarlet-text)" }}>{c.name}</p>
                <div className="flex items-center gap-2">
                  <StatusPill status={STATUS_PILL[c.status] || "expiring"} label={c.status} />
                  {a.roi_multiple != null && (
                    <span className="text-sm font-bold tabular-nums px-2 py-0.5 rounded-full"
                      style={{
                        background: a.roi_multiple >= 1 ? "rgba(5,150,105,0.12)" : "rgba(180,35,24,0.10)",
                        color: a.roi_multiple >= 1 ? "var(--scarlet-pos)" : "var(--scarlet-neg)",
                      }}>
                      {a.roi_multiple}× return
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                <Cell label="Spent" value={rupees(a.spend)} />
                <Cell label="Revenue" value={rupees(a.revenue)} strong />
                <Cell label="Cost / member" value={rupees(a.cost_per_member)} />
                <Cell label="Revenue / member" value={rupees(a.revenue_per_member)} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3" style={{ borderTop: "1px solid var(--scarlet-hairline)" }}>
                <Cell label="Still paying" value={r.still_active} />
                <Cell label="Left" value={r.churned} />
                <Cell label="No plan sold yet" value={r.no_plan_yet} />
                <Cell label="Avg time as member" value={r.avg_tenure_months != null ? `${r.avg_tenure_months} mo` : "—"} />
              </div>

              {!!a.members?.length && (
                <div className="mt-3 pt-3 space-y-1" style={{ borderTop: "1px solid var(--scarlet-hairline)" }}>
                  <p className="text-[11px] font-semibold" style={{ color: "var(--scarlet-text-secondary)" }}>
                    Members this campaign brought in
                  </p>
                  {a.members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-xs">
                      <span style={{ color: "var(--scarlet-text)" }}>
                        {m.name}
                        <span className="ml-2 text-[10px] uppercase font-bold" style={{
                          color: m.outcome === "active" ? "var(--scarlet-pos)"
                            : m.outcome === "churned" ? "var(--scarlet-neg)" : "var(--scarlet-text-secondary)",
                        }}>
                          {m.outcome === "no_plan_yet" ? "no plan yet" : m.outcome}
                        </span>
                      </span>
                      <span className="tabular-nums" style={{ color: "var(--scarlet-text-secondary)" }}>
                        {rupees(m.revenue)}{m.tenure_days != null ? ` · ${m.tenure_days}d` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
        A member counts toward a campaign when the lead they converted from carried that campaign's tag.
        Revenue is payments actually received less refunds, straight from the ledger — not an estimate.
      </p>
    </div>
  );
}

function Cell({ label, value, strong }) {
  return (
    <div>
      <p className="text-[11px]" style={{ color: "var(--scarlet-text-secondary)" }}>{label}</p>
      <p className={`tabular-nums ${strong ? "text-base font-bold" : "text-sm font-semibold"}`} style={{ color: "var(--scarlet-text)" }}>{value}</p>
    </div>
  );
}
